import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Input,
  Button,
  Space,
  Tag,
  Avatar,
  Typography,
  message,
  Popconfirm,
  Select,
  Row,
  Col,
  Image,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { postsService, Post } from '../services/postsService';
import { getImageUrl } from '../config/api';
import apiClient from '../services/apiClient';
import './Posts.css';

const { Title } = Typography;
const { Option } = Select;

interface PostFilters {
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const Posts: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<PostFilters>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  useEffect(() => {
    fetchPosts();
  }, [filters]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

      const queryString = params.toString();
      const url = queryString ? `/posts/admin?${queryString}` : '/posts/admin';
      
      const response = await apiClient.get(url);
      
      if (response.data.success) {
        const postsData = response.data.data.posts || response.data.data || [];
        setPosts(Array.isArray(postsData) ? postsData : []);
        
        if (response.data.data.pagination) {
          setPagination({
            current: response.data.data.pagination.currentPage || filters.page || 1,
            pageSize: response.data.data.pagination.itemsPerPage || filters.limit || 20,
            total: response.data.data.pagination.totalItems || 0,
          });
        }
      } else {
        setPosts([]);
      }
    } catch (error: any) {
      console.error('Error fetching posts:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to fetch posts');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setFilters({ ...filters, search: value, page: 1 });
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handleDelete = async (id: string) => {
    try {
      await apiClient.delete(`/posts/admin/${id}`);
      message.success('Post deleted successfully');
      fetchPosts();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete post');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await apiClient.patch(`/posts/admin/${id}/toggle-status`, { isActive: !currentStatus });
      message.success(`Post ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchPosts();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to toggle post status');
    }
  };

  const columns = [
    {
      title: 'Media',
      key: 'media',
      width: 100,
      render: (record: Post) => (
        record.media && record.media.length > 0 ? (
          <Image
            src={getImageUrl(record.media[0].url)}
            alt="Post"
            width={60}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            fallback="https://via.placeholder.com/60x60"
          />
        ) : (
          <div style={{ width: 60, height: 60, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <UserOutlined style={{ fontSize: 24, color: '#999' }} />
          </div>
        )
      ),
    },
    {
      title: 'User',
      key: 'user',
      width: 200,
      render: (record: Post) => (
        <Space>
          <Avatar src={getImageUrl(record.user?.profileImage || '')} icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 500 }}>{record.user?.name || '-'}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>@{record.user?.username || '-'}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Caption',
      key: 'caption',
      ellipsis: true,
      render: (record: Post) => (
        <div style={{ maxWidth: 300 }}>
          {record.caption ? (
            <span>{record.caption.length > 100 ? `${record.caption.substring(0, 100)}...` : record.caption}</span>
          ) : (
            <span style={{ color: '#999' }}>-</span>
          )}
        </div>
      ),
    },
    {
      title: 'Likes',
      dataIndex: 'likesCount',
      key: 'likesCount',
      width: 80,
      render: (count: number) => count || 0,
    },
    {
      title: 'Comments',
      dataIndex: 'commentsCount',
      key: 'commentsCount',
      width: 100,
      render: (count: number) => count || 0,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: Post) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/posts/${record._id}`)}
          >
            View
          </Button>
          <Button
            icon={record.isActive ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            size="small"
            onClick={() => handleToggleStatus(record._id, record.isActive)}
          >
            {record.isActive ? 'Hide' : 'Show'}
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this post?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="posts-page">
      <Card>
        <Row justify="space-between" align="middle" className="posts-header" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>Posts Management</Title>
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchPosts}
              loading={loading}
            >
              Refresh
            </Button>
          </Col>
        </Row>

        <div className="posts-filters" style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              placeholder="Search by caption, user..."
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: 300 }}
              onPressEnter={(e) => handleSearch(e.currentTarget.value)}
              onChange={(e) => {
                if (!e.target.value) {
                  handleSearch('');
                }
              }}
            />
            <Select
              placeholder="Status"
              allowClear
              style={{ width: 150 }}
              onChange={(value) => handleFilterChange('isActive', value)}
            >
              <Option value="true">Active</Option>
              <Option value="false">Inactive</Option>
            </Select>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={posts}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} posts`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, pageSize) => {
              setFilters({ ...filters, page, limit: pageSize });
              setPagination({ ...pagination, current: page, pageSize });
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default Posts;

